import React from 'react';
import { useSelector } from 'react-redux';

import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import { useNavigate } from 'hooks/useNavigate';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { ReactComponent as ProfileIcon } from 'images/profile-icon.svg';
import { ReactComponent as SettingsIcon } from 'images/settings-icon.svg';
import { ReactComponent as CashIcon } from 'images/cash-icon.svg';
import { infoNotification } from 'notifications';
import { showIntercom } from 'utils/intercom';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { getLoanGoal } from 'selectors/getLoanGoal';

import DashboardTileCard from 'components/FinancialCheckup/FinancialDashboard/DashboardActionTiles/DashboardTileCard';
import { DashboardTileIconTone } from 'components/FinancialCheckup/FinancialDashboard/DashboardActionTiles/types';
import { FLOW_TO_STEP_MAP, VERIFICATION_PENDING_STATUSES } from 'handlers/applicationStep';
import { hasPrimaryPayDownCreditCardDebtGoal } from 'utils/aiCoachUtils';
import { CurrentFlow } from 'enums/CurrentFlow';

import { generatePath } from 'react-router-dom';

import styles from './YourPlanneryLoanSection.module.scss';

enum CardOption {
  ResumeApplication = 'ResumeApplication',
  ContactPlannery = 'ContactPlannery',
  ManageLoan = 'ManageLoan',
  MakePayment = 'MakePayment',
}

export type LoanActionCard<T> = {
  icon: React.ReactNode;
  iconTone: DashboardTileIconTone;
  label: string;
  action: T;
};

const HIDE_LOAN_SECTION_STATUSES = [ApplicationStatusName.AwaitingFunding, ApplicationStatusName.Rejected];

const YourPlanneryLoanSection: React.FC = () => {
  const { application } = useSelector(getApplicationData);
  const { breakdowns } = useSelector(getLoanBreakdown);
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const { isAICoachFlow } = useCurrentFlow();
  const navigate = useNavigate();

  if (
    !application ||
    HIDE_LOAN_SECTION_STATUSES.includes(application.status) ||
    (isAICoachFlow &&
      application.status === ApplicationStatusName.OfferAvailable &&
      !hasPrimaryPayDownCreditCardDebtGoal(aiCoachGoals))
  )
    return null;

  const isLoanApproved = application.status === ApplicationStatusName.Funded;

  const isVerificationPending = VERIFICATION_PENDING_STATUSES.includes(application.status);

  const CARD_OPTION_ROUTES_MAP: Partial<Record<CardOption, RoutePath>> = {
    [CardOption.ResumeApplication]: FLOW_TO_STEP_MAP[CurrentFlow.DebtConsolidation](application).path,
    [CardOption.ManageLoan]: RoutePath.YourLoans,
  };

  const handleContinue = async (option: CardOption) => {
    // Handle Contact Plannery action - open Intercom
    if (option === CardOption.ContactPlannery) return showIntercom();

    // Handle Manage Loan action - navigate based on number of loans
    if (option === CardOption.ManageLoan) {
      if (breakdowns.length === 1) {
        const path = generatePath(RoutePath.ManageYourLoan, { loanId: breakdowns[0].id.toString() });
        return navigate(path as RoutePath);
      }

      return navigate(RoutePath.YourLoans);
    }

    const route = CARD_OPTION_ROUTES_MAP[option];

    if (route) {
      navigate(
        route as RoutePath,
        route === RoutePath.OfferAvailable ? OFFER_AVAILABLE_FROM_DASHBOARD_STATE : undefined,
      );
    } else {
      // Temporary placeholder for non-navigation actions
      infoNotification(`${option} - Coming soon!`);
    }
  };

  const contactPlanneryCard: LoanActionCard<CardOption> = {
    icon: <ProfileIcon />,
    iconTone: DashboardTileIconTone.Purple,
    label: 'Contact Plannery',
    action: CardOption.ContactPlannery,
  };

  const cards: LoanActionCard<CardOption>[] = isLoanApproved
    ? [
        {
          icon: <SettingsIcon />,
          iconTone: DashboardTileIconTone.Blue,
          label: `Manage your loan${breakdowns.length > 1 ? 's' : ''}`,
          action: CardOption.ManageLoan,
        },
        contactPlanneryCard,
      ]
    : [
        {
          icon: <CashIcon />,
          iconTone: DashboardTileIconTone.Green,
          label: isVerificationPending ? 'Resume application' : 'Offer status',
          action: CardOption.ResumeApplication,
        },
        contactPlanneryCard,
      ];

  const sectionTitle = `Your Plannery loan ${isLoanApproved ? '' : 'application'}`;

  return (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>{sectionTitle}</p>
      <div className={styles.cardsContainer}>
        {cards.map((card) => (
          <DashboardTileCard
            key={card.action}
            icon={card.icon}
            iconTone={card.iconTone}
            title={card.label}
            onClick={() => handleContinue(card.action)}
          />
        ))}
      </div>
    </div>
  );
};

export default YourPlanneryLoanSection;
